import { In } from 'typeorm'

import {
  getQueryBuilderFromListParams,
  getRepository,
  getTimesForPeriod,
  ListParam,
  Sorting
} from '@things-factory/shell'

import { DataSet, DataSetSummaryPeriodType } from '../service/data-set/data-set.js'
import { DataSummary } from '../service/data-summary/data-summary.js'

const STAT_FUNCTION_MAP = {
  sum: 'sum',
  mean: 'mean',
  stddev: 'standardDeviation',
  variance: 'variance',
  min: 'min',
  max: 'max',
  range: 'range',
  median: 'median',
  mode: 'mode'
}

export async function queryDataSummaryByPeriod(
  period: 'today' | 'this month' | '30 days' | 'this year' | '12 months' | 'date range' | 'month',
  dataSetName: string,
  dataKeys: string[] | null,
  params: ListParam,
  context: ResolverContext
): Promise<DataSummary[]> {
  const { domain, user, tx } = context.state
  const { t } = context

  const dataSet = await getRepository(DataSet).findOne({
    where: { domain: { id: In([domain.id, domain.parentId].filter(Boolean)) }, name: dataSetName },
    relations: ['dataKeySet']
  })

  if (!dataSet) {
    throw new Error(t('error.dataset not found', { dataSetName }))
  }

  // limitations
  const summaryPeriodType = dataSet.summaryPeriod
  if (
    (summaryPeriodType == DataSetSummaryPeriodType.Day || summaryPeriodType == DataSetSummaryPeriodType.WorkDate) &&
    period == 'today'
  ) {
    throw new Error(t('error.summary not supported', { dataSetName, period: t(`label.period-${period}`) }))
  }

  // dataKeys 가 설정되지 않았다면, dataSet의 dataKeySet을 그대로 적용한다는 의미임.
  // dataKeys == [] 라면, dataKeySet을 적용하지 않는다는 의미임.
  // dataKeys에는 dataKeySet의 dataKey 값을 따라야 한다.
  const dataKeyItems = !dataSet.dataKeySet?.dataKeyItems
    ? []
    : dataKeys
      ? dataKeys
          .map(dataKey => {
            return dataSet.dataKeySet.dataKeyItems.find(item => item.dataKey == dataKey)
          })
          .filter(Boolean)
      : dataSet.dataKeySet.dataKeyItems

  const searchables = dataKeyItems.map((item, index) => `key0${index + 1}`)
  const dataItems = dataSet.dataItems.filter(item => item.agg)

  const { date, month } = params.filters.reduce(
    (acc, filter) => {
      if (filter.name === 'date') acc.date = filter.value
      if (filter.name === 'month') acc.month = filter.value
      return acc
    },
    { date: null, month: null }
  )

  const { from, to } = await getTimesForPeriod(period, { date, month }, context)

  const selectPeriod = queryBuilder => {
    if (period == 'today') {
      queryBuilder.addSelect('summary.date', 'date').addSelect('summary.period', 'period')
    } else if (['this year', '12 months'].includes(period)) {
      queryBuilder.addSelect('SUBSTRING(summary.date, 1, 7) AS month')
    } else {
      queryBuilder.addSelect('summary.date', 'date')
    }

    return queryBuilder
  }
  const selectKeys = dataKeyItems.map((item, index) => {
    const aliasName = `key0${index + 1}`
    const columnName = `summary.${aliasName}`

    return `${columnName} as ${aliasName}`
  }) as string[]
  const selectData = dataItems.map((item, index) => {
    const aliasName = `data0${index + 1}`
    const columnName = `summary.${aliasName}`

    switch (item.agg) {
      case STAT_FUNCTION_MAP.sum:
        return `SUM(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.mean:
        // 일차 mean 된 값들을 다시 계산하므로, 무의미할 수 있다.
        return `AVG(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.stddev:
        // 일차 stddev 된 값들을 다시 계산하므로, 무의미할 수 있다.
        return `STDDEV(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.variance:
        // 일차 variance 된 값들을 다시 계산하므로, 무의미할 수 있다.
        return `VARIANCE(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.min:
        return `MIN(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.max:
        return `MAX(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.range:
        // 일차 range 값들을 다시 계산하므로, 무의미할 수 있다.
        return `MAX(${columnName}) - MIN(${columnName}) as ${aliasName}`
      case STAT_FUNCTION_MAP.mode:
        // not guaranteed
        return `MODE() WITHIN GROUP (ORDER BY ${columnName})`
      default:
        return `AVG(${columnName}) as ${aliasName}`
    }
  })

  const groupByPeriod =
    period == 'today'
      ? 'summary.date, summary.period'
      : ['this year', '12 months'].includes(period)
        ? 'SUBSTRING(summary.date, 1, 7)'
        : 'summary.date'
  const groupByKeys = dataKeyItems
    .map((item, index) => {
      return `summary.key0${index + 1}`
    })
    .join(', ')

  const desc = false
  var orderByPeriod: Sorting[] =
    period == 'today'
      ? [
          { name: 'date', desc },
          { name: 'period', desc }
        ]
      : ['this year', '12 months'].includes(period)
        ? [{ name: 'month', desc }]
        : [{ name: 'date', desc }]

  if (params?.sortings && params.sortings.length > 0) {
    orderByPeriod = params.sortings
  }

  var queryBuilder = getQueryBuilderFromListParams({
    repository: getRepository(DataSummary),
    params: {
      ...params,
      filters: params?.filters?.filter(({ name }) => name !== 'date' && name !== 'month') || [],
      sortings: []
    },
    domain,
    alias: 'summary',
    searchables: searchables
  })
    .select('summary.dataSet')
    .addSelect(selectData)
    .addSelect('SUM(summary.count)', 'count')
    .addSelect('SUM(summary.countOoc)', 'countOoc')
    .addSelect('SUM(summary.countOos)', 'countOos')

  queryBuilder = selectPeriod(queryBuilder)
    .innerJoin('summary.dataSet', 'ds', 'ds.id = :dataSetName', {
      dataSetName: dataSet.id
    })
    .andWhere('summary.domain = :domain', { domain: domain.id })
    .andWhere('summary.date >= :from', { from })
    .andWhere('summary.date < :to', { to })
    .addGroupBy('summary.dataSet')
    .addGroupBy(groupByPeriod)

  if (dataKeyItems.length > 0) {
    queryBuilder.addSelect(selectKeys).addGroupBy(groupByKeys)
  }

  orderByPeriod.map(orderBy => {
    const { name: sort, desc } = orderBy
    queryBuilder.addOrderBy(sort, desc ? 'DESC' : 'ASC')
  })

  const items = await queryBuilder.getRawMany()

  return items
}
